/**
 * Mega Header behavior.
 * - Uses hover-intent (small close delay) on devices that support real hover,
 *   so moving the mouse from the link to the panel doesn't flicker-close it.
 * - Falls back to click/tap toggling on touch devices (hover:none), so it
 *   never gets "stuck open" on mobile/tablet.
 * - Fully keyboard accessible: Enter/Space toggles, Escape closes, focus
 *   moving outside the item closes it.
 * - No inline styles are toggled from JS — only data-attributes/classes —
 *   so all visual states stay owned by CSS and can't drift out of sync.
 */
(function () {
  var CLOSE_DELAY = 150;

  document.querySelectorAll('[data-mega-header]').forEach(function (root) {
    var hasHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-mh-item]'));
    var scrim = root.querySelector('[data-mh-scrim]');
    var closeTimer = null;

    // Fixed positioning takes the header out of document flow, so a spacer
    // (sibling element, see the .liquid file) replicates the space it would
    // otherwise occupy. Its height is measured from the real rendered header
    // — not hardcoded — since the header's height is fluid (clamp()-based)
    // and changes across screen sizes.
    var spacer = root.nextElementSibling;
    if (spacer && spacer.hasAttribute('data-mh-spacer') && root.getAttribute('data-sticky') === 'true') {
      function syncSpacerHeight() {
        spacer.style.height = root.offsetHeight + 'px';
      }
      syncSpacerHeight();
      window.addEventListener('resize', syncSpacerHeight);
      // Fonts loading async can change the header's height after first paint.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(syncSpacerHeight);
      }
    }

    function closeAll(except) {
      items.forEach(function (item) {
        if (item === except) return;
        item.removeAttribute('data-open');
        var trigger = item.querySelector('[data-mh-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      if (!except) root.removeAttribute('data-panel-open');
    }

    function openItem(item) {
      clearTimeout(closeTimer);
      closeAll(item);
      item.setAttribute('data-open', '');
      root.setAttribute('data-panel-open', '');
      var trigger = item.querySelector('[data-mh-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        closeAll();
      }, CLOSE_DELAY);
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-mh-trigger]');
      if (!trigger) return;

      if (hasHover) {
        item.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
          openItem(item);
        });
        item.addEventListener('mouseleave', scheduleClose);
      } else {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          var isOpen = item.hasAttribute('data-open');
          if (isOpen) {
            closeAll();
          } else {
            openItem(item);
          }
        });
      }

      // Keyboard support works the same regardless of pointer type.
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          var isOpen = item.hasAttribute('data-open');
          if (isOpen) {
            closeAll();
          } else {
            openItem(item);
          }
        }
        if (e.key === 'Escape') {
          closeAll();
          trigger.focus();
        }
      });
    });

    // ---- Mobile drawer (declared before the global handlers below use it) ----
    var burger = root.querySelector('[data-mh-burger]');
    var mobile = root.querySelector('[data-mh-mobile]');

    function closeDrawer() {
      if (!root.hasAttribute('data-mobile-open')) return;
      root.removeAttribute('data-mobile-open');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      if (mobile) mobile.setAttribute('aria-hidden', 'true');
    }

    if (burger && mobile) {
      burger.addEventListener('click', function (e) {
        e.stopPropagation(); // keep the document-level outside-click handler from immediately re-closing it
        var isOpen = root.hasAttribute('data-mobile-open');
        if (isOpen) {
          closeDrawer();
        } else {
          root.setAttribute('data-mobile-open', '');
          burger.setAttribute('aria-expanded', 'true');
          mobile.setAttribute('aria-hidden', 'false');
        }
      });
    }

    // Close when focus leaves the whole header (tabbing past it).
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) closeAll();
    });

    // Click/tap anywhere outside closes panels AND the mobile drawer.
    // For the drawer, "outside" means anywhere that isn't the drawer itself
    // or the burger button (the burger has its own toggle handler).
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeAll();
      if (root.hasAttribute('data-mobile-open')) {
        var insideDrawer = mobile && mobile.contains(e.target);
        var onBurger = burger && burger.contains(e.target);
        if (!insideDrawer && !onBurger) closeDrawer();
      }
    });

    // Escape anywhere closes panels and drawer.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeAll(); closeDrawer(); }
    });

    if (scrim) {
      scrim.addEventListener('click', function () {
        closeAll();
        closeDrawer();
      });
    }

    // If the viewport crosses the desktop/mobile breakpoint (e.g. rotating
    // a tablet, or resizing a browser window), clear any open state so nothing
    // gets left open in the wrong layout.
    var mq = window.matchMedia('(min-width: 990px)');
    function handleBreakpointChange() {
      closeAll();
      root.removeAttribute('data-mobile-open');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      if (mobile) mobile.setAttribute('aria-hidden', 'true');
    }
    if (mq.addEventListener) {
      mq.addEventListener('change', handleBreakpointChange);
    } else if (mq.addListener) {
      mq.addListener(handleBreakpointChange); // Safari < 14 fallback
    }
  });
})();

/**
 * Live cart count.
 * The badge is server-rendered by Liquid, so on its own it only updates on a
 * full page reload. Horizon adds items via AJAX (fetch to /cart/add.js etc.),
 * which never reloads the page — so the badge went stale. This module watches
 * for any cart-mutating request (fetch or XHR, so it also catches apps and
 * theme scripts we don't control), then re-reads /cart.js and updates every
 * [data-mh-cart-count] badge in place.
 */
(function () {
  var CART_MUTATION = /\/cart\/(add|change|update|clear)/;

  function refreshCartCount() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-mh-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('mh__cart-count--empty', cart.item_count === 0);
        });
      })
      .catch(function () { /* network hiccup: keep the current badge rather than blanking it */ });
  }

  // Catch fetch-based cart mutations (Horizon and most modern themes/apps).
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = '';
    try {
      url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    } catch (e) {}
    var result = origFetch.apply(this, arguments);
    if (CART_MUTATION.test(url)) {
      result.then(function () { refreshCartCount(); }, function () {});
    }
    return result;
  };

  // Catch XHR-based cart mutations (older apps/scripts).
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && CART_MUTATION.test(url)) {
      this.addEventListener('loadend', refreshCartCount);
    }
    return origOpen.apply(this, arguments);
  };

  // Common custom events various themes/apps dispatch after cart changes.
  ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh'].forEach(function (evt) {
    document.addEventListener(evt, refreshCartCount);
  });

  // Back/forward navigation restored from bfcache shows a stale page snapshot.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refreshCartCount();
  });
})();


